import {css, StyleSheet} from 'aphrodite';
import stringify from 'json-stable-stringify';
import React from 'react';
import isEqual from 'react-fast-compare';

import ExportAuditLogButton from 'audit/ExportAuditLogButton';
import {Policy, PolicySpec, PolicyStatement} from 'auth/PolicyConstants';
import {PolicyTemplateNode} from 'graphql-client/__generated/graphqlGlobalTypes';
import navigateToURL from 'lib/navigateToURL';
import SettingsPageContainer from 'settings/SettingsPageContainer';
import SaveBanner from 'ui-library/banners/SaveBanner';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import TextField from 'ui-library/inputs/TextField';
import sortedBy from 'util/sortedBy';
import {InputChangeEvent} from 'util/Types';

import PolicyStatementsEditor from './PolicyStatementsEditor';

const styles = StyleSheet.create({
  container: {
    height: '100%',
    paddingBottom: 40,
  },
});

export interface PolicyEditorProps {
  policy: Omit<Policy, 'isHidden'>;
  initialSpec: PolicySpec;
  spec: PolicySpec;
  isWritable: boolean;
  listingsTitle: string;
  policiesURL: string;
  policyTemplate: PolicyTemplateNode;
  onUpdateSpec: (specUpdate: Partial<PolicySpec>) => void;
  onSaveSpec: () => {};
}

function getComparableSpec(spec: PolicySpec): PolicySpec {
  return {
    ...spec,
    statements: sortedBy(spec.statements, [(statement: PolicyStatement) => stringify(statement)]),
  };
}

class PolicyEditor extends React.Component<PolicyEditorProps> {
  _navigateToPoliciesListing(): void {
    navigateToURL(this.props.policiesURL);
  }

  _handleChangeName = (e: InputChangeEvent): void => {
    const name = e.target.value;
    this.props.onUpdateSpec({
      name,
    });
  };

  _hasChanges(): boolean {
    return !isEqual(getComparableSpec(this.props.initialSpec), getComparableSpec(this.props.spec));
  }

  _handleDiscardChanges = (): void => {
    this.props.onUpdateSpec(this.props.initialSpec);
  };

  _handleUpdatePolicyStatements = (policyStatements: Array<PolicyStatement>): void => {
    this.props.onUpdateSpec({
      statements: policyStatements,
    });
  };

  render(): JSX.Element {
    return (
      <div className={css(styles.container)}>
        {this._hasChanges() && (
          <SaveBanner
            text="Permissions changed"
            onSave={this.props.onSaveSpec}
            onDiscard={this._handleDiscardChanges}
          />
        )}
        <SettingsPageContainer
          onClickTitle={() => {
            this._navigateToPoliciesListing();
          }}
          subtitle={this.props.policy.name}
          title={this.props.listingsTitle}
          toolbar={<ExportAuditLogButton ids={[this.props.policy.id]} />}
        >
          <div>
            {this.props.isWritable && (
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <TextField label="Name" onChange={this._handleChangeName} value={this.props.spec.name} />
                  </FormGroup>
                </Col>
              </Row>
            )}
            <PolicyStatementsEditor
              policyTemplate={this.props.policyTemplate}
              policyStatements={this.props.spec.statements}
              onUpdateStatements={this._handleUpdatePolicyStatements}
              isWritable={this.props.isWritable}
            />
          </div>
        </SettingsPageContainer>
      </div>
    );
  }
}

export default PolicyEditor;
